import { TaxonomyTermSuggestionSource } from '@edgebox/sync-core-data-definitions';

/**
 * Turning a suggestion's evidence count into a link to that evidence.
 *
 * A suggested term arrives with "seen in N answers" attached, and an operator deciding whether to
 * approve it wants to read those answers rather than trust the number. The answers list can already
 * be filtered to the evidence behind one kind of suggestion, so that count becomes a link.
 */

/** The AI-answers list, filtered. */
const ANSWERS_PATH = '/brand-presence/analytics/ai-answers';

/**
 * The co-subject filter matches the run mirror's `norm`, so the name has to be normalized the same
 * way the mirror normalized it. Kept as its own function rather than inlined so the two cannot
 * drift: a display spelling sent to that filter matches nothing and returns an empty list, which
 * reads as "no evidence" for a suggestion that plainly has some.
 */
function normalizeCoSubjectName(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Where to send a reader who wants the answers behind a suggestion, or undefined when we cannot
 * take them anywhere truthful.
 *
 * ASPECT suggestions link to the attribute filter, which selects runs whose `aspectSummaries[]`
 * carry the term — precisely the evidence the suggestion was counted from.
 *
 * CO-SUBJECT suggestions link to the untracked-co-subject filter, which reads the run mirror that
 * recorded them. Keyed by the term's NAME rather than its key: the mirror stored what the answer
 * wrote, at a time when no term existed for it.
 *
 * Anything else returns undefined rather than a bare path. A link that dropped its filter would
 * open an unfiltered list looking exactly like a filtered one, presenting the whole corpus as the
 * evidence for one suggestion — worse than offering no link at all.
 */
export function suggestionAnswersPath(
  source: TaxonomyTermSuggestionSource | undefined,
  termKey: string | undefined,
  termName?: string
): string | undefined {
  if (source === TaxonomyTermSuggestionSource.Aspect) {
    const key = termKey?.trim();
    return key ? `${ANSWERS_PATH}?${new URLSearchParams({ attribute: key }).toString()}` : undefined;
  }

  if (source === TaxonomyTermSuggestionSource.CoSubject) {
    const norm = normalizeCoSubjectName(termName ?? '');
    return norm ? `${ANSWERS_PATH}?${new URLSearchParams({ coSubject: norm }).toString()}` : undefined;
  }

  return undefined;
}
